import React, { useState, useEffect } from "react";
import { motion, AnimatePresence, LayoutGroup } from "framer-motion";
import { Plus, Minus, ArrowRight } from "@phosphor-icons/react";
import { PRACTICE_AREAS } from "../lib/practiceAreas";

const AREA_IMAGES = {
  immigration: "https://images.unsplash.com/photo-1569163139394-de4798aa62b6?auto=format&fit=crop&w=900&q=80",
  family: "https://images.unsplash.com/photo-1511895426328-dc8714191300?auto=format&fit=crop&w=900&q=80",
  crime: "https://images.unsplash.com/photo-1589994965851-a8f479c573a9?auto=format&fit=crop&w=900&q=80",
  employment: "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=900&q=80",
  civil: "https://images.unsplash.com/photo-1521587760476-6c12a4b040da?auto=format&fit=crop&w=900&q=80",
};

const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.04, delayChildren: 0.1 },
  },
};
const itemVariants = {
  hidden: { opacity: 0, x: -12 },
  show: { opacity: 1, x: 0, transition: { duration: 0.35, ease: "easeOut" } },
};

export const PracticeAreas = () => {
  const [active, setActive] = useState(PRACTICE_AREAS[0].id);
  const current = PRACTICE_AREAS.find((p) => p.id === active) || PRACTICE_AREAS[0];

  // Preload all area images on mount for snappy transitions
  useEffect(() => {
    Object.values(AREA_IMAGES).forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }, []);

  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative py-24 md:py-32 bg-[#0B0B0E] border-t border-white/5 section-light"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
          className="grid lg:grid-cols-12 gap-10 mb-16"
        >
          <div className="lg:col-span-6">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-12 bg-[#E9C76A]" />
              <span className="overline">What we do</span>
            </div>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-[#F5F2E8]">
              Five disciplines.
              <br />
              <span className="italic gold-gradient-text">One</span> standard of care.
            </h2>
          </div>
          <div className="lg:col-span-5 lg:col-start-8 self-end">
            <p className="text-base md:text-lg text-white/60 font-light leading-relaxed">
              Whether you&apos;re settling status, buying a home, or defending your liberty —
              our partners handle your matter personally, from first call to final outcome.
            </p>
          </div>
        </motion.div>

        {/* Desktop split view */}
        <LayoutGroup>
          <div className="hidden lg:grid lg:grid-cols-12 border-t border-white/5">
            {/* Index list */}
            <div className="lg:col-span-5 border-r border-white/5 relative">
              {PRACTICE_AREAS.map((area, idx) => {
                const isActive = area.id === active;
                return (
                  <button
                    key={area.id}
                    data-testid={`area-tab-${area.id}`}
                    onClick={() => setActive(area.id)}
                    className="group relative w-full text-left px-2 py-7 border-b border-white/5 flex items-baseline gap-6 transition-colors hover:bg-[#14141A]"
                  >
                    {isActive && (
                      <motion.span
                        layoutId="area-active-bg"
                        className="absolute inset-0 bg-gradient-to-r from-[#E9C76A]/[0.06] to-transparent"
                        transition={{ type: "spring", stiffness: 300, damping: 30 }}
                      />
                    )}
                    {isActive && (
                      <motion.span
                        layoutId="area-active-bar"
                        className="absolute left-0 top-0 bottom-0 w-[2px] bg-gradient-to-b from-[#F0D17A] to-[#A8821C]"
                        transition={{ type: "spring", stiffness: 300, damping: 30 }}
                      />
                    )}
                    <span
                      className={`relative font-sans text-xs tracking-[0.25em] transition-colors ${
                        isActive ? "text-[#E9C76A]" : "text-white/30"
                      }`}
                    >
                      {String(idx + 1).padStart(2, "0")}
                    </span>
                    <div className="relative flex-1">
                      <div
                        className={`font-serif text-2xl md:text-3xl tracking-tight transition-colors ${
                          isActive
                            ? "text-[#F5F2E8]"
                            : "text-white/55 group-hover:text-white/85"
                        }`}
                      >
                        {area.title}
                      </div>
                    </div>
                    <ArrowRight
                      size={20}
                      weight="thin"
                      className={`relative transition-all duration-300 ${
                        isActive
                          ? "text-[#E9C76A] translate-x-1"
                          : "text-white/20 group-hover:text-white/60 group-hover:translate-x-0.5"
                      }`}
                    />
                  </button>
                );
              })}
            </div>

            {/* Detail panel */}
            <div className="lg:col-span-7 relative min-h-[560px] overflow-hidden">
              <AnimatePresence mode="wait">
                <motion.div
                  key={current.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.4 }}
                  className="absolute inset-0"
                >
                  {/* Background image */}
                  <motion.div
                    initial={{ scale: 1.08, opacity: 0 }}
                    animate={{ scale: 1, opacity: 0.18 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.9, ease: "easeOut" }}
                    className="absolute inset-0 bg-cover bg-center"
                    style={{ backgroundImage: `url(${AREA_IMAGES[current.id]})` }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-[#0B0B0E] via-[#0B0B0E]/85 to-[#0B0B0E]/40" />
                  {/* Sheen */}
                  <div
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      background:
                        "radial-gradient(ellipse 60% 50% at 80% 10%, rgba(233,199,106,0.12), transparent 60%)",
                    }}
                  />
                </motion.div>
              </AnimatePresence>

              <AnimatePresence mode="wait">
                <motion.div
                  key={`content-${current.id}`}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.4, ease: "easeOut" }}
                  className="relative p-10 lg:p-14"
                >
                  <div className="overline mb-4">{current.overline}</div>
                  <h3 className="font-serif text-3xl md:text-4xl text-[#F5F2E8] mb-5 tracking-tight">
                    {current.title}
                  </h3>
                  <p className="text-white/65 leading-relaxed mb-10 max-w-xl">
                    {current.description}
                  </p>
                  <motion.ul
                    variants={containerVariants}
                    initial="hidden"
                    animate="show"
                    className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3"
                  >
                    {current.services.map((s) => (
                      <motion.li
                        key={s}
                        variants={itemVariants}
                        className="flex items-start gap-3 text-sm text-white/80 font-light"
                      >
                        <span className="mt-2 h-px w-3 bg-[#E9C76A] flex-shrink-0" />
                        <span>{s}</span>
                      </motion.li>
                    ))}
                  </motion.ul>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </LayoutGroup>

        {/* Mobile accordion */}
        <div className="lg:hidden border-t border-white/5">
          {PRACTICE_AREAS.map((area, idx) => {
            const isOpen = area.id === active;
            return (
              <div key={area.id} className="border-b border-white/5">
                <button
                  data-testid={`mobile-area-${area.id}`}
                  onClick={() => setActive(isOpen ? null : area.id)}
                  className="w-full flex items-center justify-between py-6"
                >
                  <div className="flex items-baseline gap-4">
                    <span className="text-xs text-[#E9C76A] tracking-[0.2em]">
                      {String(idx + 1).padStart(2, "0")}
                    </span>
                    <span className="font-serif text-xl text-[#F5F2E8] text-left">
                      {area.title}
                    </span>
                  </div>
                  {isOpen ? (
                    <Minus size={20} weight="thin" className="text-[#E9C76A]" />
                  ) : (
                    <Plus size={20} weight="thin" className="text-white/50" />
                  )}
                </button>
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.35, ease: "easeOut" }}
                      className="overflow-hidden"
                    >
                      <div className="pb-7 pr-2">
                        <p className="text-sm text-white/60 leading-relaxed mb-5">
                          {area.description}
                        </p>
                        <motion.ul
                          variants={containerVariants}
                          initial="hidden"
                          animate="show"
                          className="space-y-2"
                        >
                          {area.services.map((s) => (
                            <motion.li
                              key={s}
                              variants={itemVariants}
                              className="flex items-start gap-3 text-sm text-white/75"
                            >
                              <span className="mt-2 h-px w-3 bg-[#E9C76A] flex-shrink-0" />
                              {s}
                            </motion.li>
                          ))}
                        </motion.ul>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
